import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { AuthImage } from "@/components/AuthImage";
import { CommentThread } from "@/components/CommentThread";
import { Baby, IndianRupee, CalendarCheck, Camera, Award, ArrowRight } from "lucide-react";

export default function ParentPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [child, setChild] = useState(null);
  const [fees, setFees] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [attendance, setAttendance] = useState([]);

  useEffect(() => {
    if (!user?.linked_child_id) return;
    const cid = user.linked_child_id;
    Promise.all([
      api.get(`/children/${cid}`),
      api.get("/fees"),
      api.get("/daily-updates"),
      api.get(`/milestones/${cid}`),
      api.get(`/attendance/child/${cid}`),
    ]).then(([c, f, u, m, a]) => {
      setChild(c.data);
      setFees(f.data);
      setUpdates(u.data);
      setMilestones(m.data);
      setAttendance(a.data);
    }).catch(() => {});
  }, [user]);

  if (!user?.linked_child_id) {
    return <div className="py-16 text-center text-muted-foreground">Your account isn't linked to a child yet. Please contact the school.</div>;
  }
  if (!child) return <div className="py-16 text-center text-muted-foreground">Loading...</div>;

  const attPct = attendance.length
    ? Math.round(attendance.filter((a) => a.status === "Present").length / attendance.length * 100)
    : 0;
  const dues = fees.filter((f) => !f.paid);

  return (
    <div className="space-y-6">
      <div className="card-soft rounded-3xl p-6 md:p-8">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-20 h-20 rounded-3xl chip-pink flex items-center justify-center text-3xl font-black">
            {child.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold uppercase tracking-widest text-primary/80">Your little one</div>
            <h1 className="text-3xl font-black">{child.name}</h1>
            <div className="text-muted-foreground font-semibold">{child.class_name} · Admitted {child.admission_date}</div>
          </div>
          <button
            onClick={() => nav(`/children/${child.id}`)}
            className="chip-green px-4 py-2 rounded-full font-bold text-sm flex items-center gap-1"
          >
            Full profile <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-soft rounded-3xl p-5">
          <CalendarCheck className="text-primary mb-2" strokeWidth={2.5} />
          <div className="text-3xl font-black">{attPct}%</div>
          <div className="text-xs font-bold uppercase text-muted-foreground">Attendance</div>
        </div>
        <div className="card-soft rounded-3xl p-5">
          <IndianRupee className="text-primary mb-2" strokeWidth={2.5} />
          <div className="text-3xl font-black">₹{dues.reduce((s, f) => s + f.amount, 0).toLocaleString()}</div>
          <div className="text-xs font-bold uppercase text-muted-foreground">Dues</div>
        </div>
        <div className="card-soft rounded-3xl p-5">
          <Award className="text-primary mb-2" strokeWidth={2.5} />
          <div className="text-3xl font-black">{milestones.filter((m) => m.achieved).length}<span className="text-lg text-muted-foreground">/{milestones.length}</span></div>
          <div className="text-xs font-bold uppercase text-muted-foreground">Milestones</div>
        </div>
        <div className="card-soft rounded-3xl p-5">
          <Camera className="text-primary mb-2" strokeWidth={2.5} />
          <div className="text-3xl font-black">{updates.length}</div>
          <div className="text-xs font-bold uppercase text-muted-foreground">Updates</div>
        </div>
      </div>

      <Card className="rounded-3xl">
        <CardHeader><CardTitle className="text-xl font-extrabold flex items-center gap-2"><Camera className="text-primary" size={20} /> Recent Classroom Updates</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {updates.length === 0 && <div className="text-muted-foreground text-center py-6">No updates yet.</div>}
          {updates.slice(0, 5).map((u) => (
            <div key={u.id} className="rounded-2xl border border-border p-4">
              <div className="text-xs font-bold text-muted-foreground uppercase">{u.date} · {u.posted_by}</div>
              <p className="mt-2">{u.activity_note}</p>
              {u.meal_note && <p className="text-sm text-muted-foreground mt-1">🍎 {u.meal_note}</p>}
              {u.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {u.photos.map((p) => <AuthImage key={p} path={p} className="w-full aspect-square object-cover rounded-xl" />)}
                </div>
              )}
              <CommentThread updateId={u.id} />
            </div>
          ))}
        </CardContent>
      </Card>

      {dues.length > 0 && (
        <Card className="rounded-3xl">
          <CardHeader><CardTitle className="text-xl font-extrabold flex items-center gap-2"><IndianRupee className="text-primary" size={20} /> Pending Fees</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {dues.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-3 rounded-2xl bg-muted/40">
                <div>
                  <div className="font-bold">{f.title}</div>
                  <div className="text-xs text-muted-foreground">Due {f.due_date}</div>
                </div>
                <div className="font-black">₹{f.amount.toLocaleString()}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
